/**
 * Done. / ChiroCPD — ExportSummary Feature Component
 *
 * "N of 8 fields ready to copy" headline + dot mini-map.
 * role="status" aria-live="polite" — updates announced to screen readers.
 * Dots are aria-hidden (decorative); screen reader reads the text count.
 */

import type { ExportFieldData } from '@/lib/types';
import { cx } from '@/lib/utils';
import { PORTAL_FIELD_COUNT } from '@/lib/types';

interface ExportSummaryProps {
  fields: Pick<ExportFieldData, 'fieldName' | 'fieldLabel' | 'status'>[];
}

const DOT_STYLES = {
  complete: 'bg-[var(--color-success-500)]',
  partial:  'bg-[var(--color-cpd-amber)] opacity-60',
  empty:    'bg-[var(--border-strong)] opacity-40',
} as const;

export function ExportSummary({ fields }: ExportSummaryProps): React.ReactElement {
  const completeCount = fields.filter(f => f.status === 'complete').length;
  const total = PORTAL_FIELD_COUNT;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="flex items-center justify-between gap-4 flex-wrap"
    >
      <p className="text-sm font-medium text-[var(--text-secondary)]">
        <span className="text-lg font-bold text-[var(--text-primary)]">{completeCount}</span>
        {' '}of{' '}
        <span className="font-semibold">{total}</span>
        {' '}fields ready to copy
      </p>

      {/* Dot mini-map — decorative, aria-hidden */}
      <div aria-hidden="true" className="flex items-center gap-1.5">
        {Array.from({ length: total }).map((_, i) => {
          const field = fields[i];
          const status = field?.status ?? 'empty';
          return (
            <span
              key={i}
              title={field ? `${field.fieldLabel}: ${status}` : `Field ${String(i + 1)}: empty`}
              className={cx(
                'h-3 w-3 rounded-full transition-colors',
                DOT_STYLES[status],
              )}
            />
          );
        })}
      </div>
    </div>
  );
}
